<script setup lang="ts">
import { computed } from 'vue';
import type { FieldSchema } from '../../../types';

// A color stored as a `#rrggbb` hex string, or '' once cleared. COLOR_SELECTOR is not enough for a
// stored theme color: its palette mode saves color names, and it forces red when the value is empty.

const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    error?: string | boolean;
    field?: FieldSchema;
    placeholder?: string;
  }>(),
  {
    placeholder: '#1f6f8b',
  }
);

const HEX_COLOR_PATTERN = /^#[0-9a-f]{6}$/i;

const model = defineModel<string>();

const isDisabled = computed(() => !!props.field?.state?.disabled || props.disabled);
const pickerColor = computed(() => (HEX_COLOR_PATTERN.test(model.value ?? '') ? model.value?.slice(1) : '000000'));

// EVENTS
function onPickerUpdate(color: string) {
  model.value = `#${color}`;
}

function onTextUpdate(value: string | undefined) {
  model.value = (value ?? '').trim();
}

function onClear() {
  model.value = '';
}
</script>

<template>
  <div class="flex items-center gap-3">
    <ColorPicker
      format="hex"
      :disabled="isDisabled"
      :modelValue="pickerColor"
      @update:modelValue="onPickerUpdate"
    />

    <InputText
      maxlength="7"
      :class="['h-9 w-32 text-sm', { 'af-field-error': !!error }]"
      :disabled="isDisabled"
      :invalid="!!error"
      :modelValue="model"
      :placeholder
      @update:modelValue="onTextUpdate"
    />

    <Button
      v-if="model"
      text
      icon="pi pi-times"
      severity="secondary"
      size="small"
      type="button"
      :aria-label="$t('common.clear')"
      :disabled="isDisabled"
      @click="onClear"
    />
  </div>
</template>
